import useLenders from '@/hooks/use-lenders';
import useUsers from '@/hooks/use-users';
import type { Deal } from '@/models/registry';
import {
  DeliveryPreferences,
  DeliveryStages,
  FundingMethods,
  Priorities,
  RapidReconSteps,
  Statuses,
} from '@/models/types';
import { isEqual, isString } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CREATED_DATE_FILTER_OPTIONS,
  DELIVERY_DATE_FILTER_OPTIONS,
  type FilterState,
  type FilterType,
  LAST_UPDATED_DATE_FILTER_OPTIONS,
  type MatchPreference,
  PICKUP_DATE_FILTER_OPTIONS,
} from '../types';
import useFilteredDeals, {
  type Accumulator,
  DealScope,
  DefaultAccumulator,
  type FilterOption,
} from './use-filtered-deals';

type GetCountFn = (filterType: FilterType, filterValue: string) => number;

type DealLike = {
  id: Deal['id'];
};

type FilteredDealsContextType = {
  accumulator: Accumulator;
  getCount: GetCountFn;
  filters: FilterState[];
  setFilters: React.Dispatch<React.SetStateAction<FilterState[]>>;
  matchPreference: MatchPreference;
  setMatchPreference: React.Dispatch<React.SetStateAction<MatchPreference>>;
  dealIdsMatchingFilters: Set<Deal['id']>;
  shouldApplyFilteredDealsIds: boolean;
  filterCallback: (dealLike: DealLike) => boolean;
};

const DefaultFilteredDealsContext: FilteredDealsContextType = {
  accumulator: DefaultAccumulator,
  getCount: () => 0,
  filters: [],
  setFilters: () => {},
  matchPreference: 'match-any-filter',
  setMatchPreference: () => {},
  dealIdsMatchingFilters: new Set<Deal['id']>(),
  shouldApplyFilteredDealsIds: false,
  filterCallback: () => {
    return false;
  },
};

const FilteredDealsContext = createContext<FilteredDealsContextType>(
  DefaultFilteredDealsContext,
);

export const FilteredDealsProvider: React.FC<{
  children: React.ReactNode;
  initialFilteredState?: FilterState[];
  initialMatchPreference?: MatchPreference;
  scope?: DealScope;
}> = observer(
  ({
    children,
    initialFilteredState = [],
    initialMatchPreference = 'match-any-filter',
    scope = DealScope.Active,
  }) => {
    const { accumulator } = useFilteredDeals({ scope });
    const previousAccumulator = useRef<Accumulator>(accumulator);
    const accumulatorAndPreviousAccumulatorAreEqual = useMemo(
      () => isEqual(accumulator, previousAccumulator.current),
      [accumulator, previousAccumulator.current],
    );
    const [filters, setFilters] = useState<FilterState[]>(initialFilteredState);
    const [matchPreference, setMatchPreference] = useState<MatchPreference>(
      initialMatchPreference,
    );

    const getCount = useCallback(
      (filterType: FilterType, filterValue: string) => {
        return accumulator?.[filterType]?.[filterValue]?.size ?? 0;
      },
      [accumulator],
    );

    const { financeManagers, salesReps } = useUsers();
    const { lenders } = useLenders();

    // TODO: TECH DEBT: consolidate this with the getFilterOptionsAndMatchFunctions in use-filtered-deals.ts
    const getFilterOptions = useCallback(
      (
        filterType: FilterType,
      ): {
        filterOptions: FilterOption[];
        matchFunction: (
          filterOption: FilterOption,
          filter: FilterState,
        ) => boolean;
        matchInAccumulator?: (
          filter: FilterState,
          filterOption: FilterOption,
        ) => Set<Deal['id']>;
      } => {
        switch (filterType) {
          case 'status':
            return {
              filterOptions: Statuses,
              matchFunction: (
                filterOption: FilterOption,
                filter: FilterState,
              ) => {
                if (filter.filterMetaState.isNegated) {
                  return !filter.selectedFilters[
                    filterOption as keyof typeof filter.selectedFilters
                  ];
                }

                return filter.selectedFilters[
                  filterOption as keyof typeof filter.selectedFilters
                ];
              },
            };
          case 'rapid_recon_step':
            return {
              filterOptions: RapidReconSteps,
              matchFunction: (
                filterOption: FilterOption,
                filter: FilterState,
              ) => {
                if (filter.filterMetaState.isNegated) {
                  return !filter.selectedFilters[
                    filterOption as keyof typeof filter.selectedFilters
                  ];
                }

                return filter.selectedFilters[
                  filterOption as keyof typeof filter.selectedFilters
                ];
              },
            };
          case 'delivery_stage':
            return {
              filterOptions: DeliveryStages,
              matchFunction: (
                filterOption: FilterOption,
                filter: FilterState,
              ) => {
                if (filter.filterMetaState.isNegated) {
                  return !filter.selectedFilters[
                    filterOption as keyof typeof filter.selectedFilters
                  ];
                }

                return filter.selectedFilters[
                  filterOption as keyof typeof filter.selectedFilters
                ];
              },
            };
          case 'priority':
            return {
              filterOptions: Priorities,
              matchFunction: (
                filterOption: FilterOption,
                filter: FilterState,
              ) => {
                if (filter.filterMetaState.isNegated) {
                  return !filter.selectedFilters[
                    filterOption as keyof typeof filter.selectedFilters
                  ];
                }

                return filter.selectedFilters[
                  filterOption as keyof typeof filter.selectedFilters
                ];
              },
            };
          case 'delivery_preference':
            return {
              filterOptions: DeliveryPreferences,
              matchFunction: (
                filterOption: FilterOption,
                filter: FilterState,
              ) => {
                if (filter.filterMetaState.isNegated) {
                  return !filter.selectedFilters[
                    filterOption as keyof typeof filter.selectedFilters
                  ];
                }

                return filter.selectedFilters[
                  filterOption as keyof typeof filter.selectedFilters
                ];
              },
            };
          case 'finance_manager':
            return {
              filterOptions: financeManagers.map((manager) => manager.id),
              matchFunction: (
                filterOption: FilterOption,
                filter: FilterState,
              ) => {
                if (filter.filterMetaState.isNegated) {
                  return !filter.selectedFilters[
                    filterOption as keyof typeof filter.selectedFilters
                  ];
                }

                return filter.selectedFilters[
                  filterOption as keyof typeof filter.selectedFilters
                ];
              },
            };
          case 'sales_rep':
            return {
              filterOptions: salesReps.map((rep) => rep.id),
              matchFunction: (
                filterOption: FilterOption,
                filter: FilterState,
              ) => {
                if (filter.filterMetaState.isNegated) {
                  return !filter.selectedFilters[
                    filterOption as keyof typeof filter.selectedFilters
                  ];
                }

                return filter.selectedFilters[
                  filterOption as keyof typeof filter.selectedFilters
                ];
              },
            };
          case 'funding_method':
            return {
              filterOptions: FundingMethods,
              matchFunction: (
                filterOption: FilterOption,
                filter: FilterState,
              ) => {
                if (filter.filterMetaState.isNegated) {
                  return !filter.selectedFilters[
                    filterOption as keyof typeof filter.selectedFilters
                  ];
                }

                return filter.selectedFilters[
                  filterOption as keyof typeof filter.selectedFilters
                ];
              },
            };
          case 'lender':
            return {
              filterOptions: lenders.map((lender) => lender.id),
              matchFunction: (
                filterOption: FilterOption,
                filter: FilterState,
              ) => {
                if (filter.filterMetaState.isNegated) {
                  return !filter.selectedFilters[
                    filterOption as keyof typeof filter.selectedFilters
                  ];
                }

                return filter.selectedFilters[
                  filterOption as keyof typeof filter.selectedFilters
                ];
              },
            };
          case 'delivery_queued':
            return {
              filterOptions: ['queued', 'unqueued'],
              matchFunction: () => {
                return true;
              },
              matchInAccumulator: (filter: FilterState) => {
                if (filter.filterMetaState.isNegated) {
                  return accumulator[filter.filterMetaState.type]['unqueued'];
                }

                return accumulator[filter.filterMetaState.type]['queued'];
              },
            };
          case 'price_locked':
            return {
              filterOptions: ['locked', 'unlocked'],
              matchFunction: () => {
                return true;
              },
              matchInAccumulator: (filter: FilterState) => {
                if (filter.filterMetaState.isNegated) {
                  return accumulator[filter.filterMetaState.type]['unlocked'];
                }

                return accumulator[filter.filterMetaState.type]['locked'];
              },
            };
          case 'traded_in':
            return {
              filterOptions: ['yes', 'no'],
              matchFunction: () => {
                return true;
              },
              matchInAccumulator: (filter: FilterState) => {
                if (filter.filterMetaState.isNegated) {
                  return accumulator[filter.filterMetaState.type]['no'];
                }

                return accumulator[filter.filterMetaState.type]['yes'];
              },
            };
          case 'delivery_date':
            return {
              filterOptions: DELIVERY_DATE_FILTER_OPTIONS.map(
                (option) => option.value,
              ),
              matchFunction: (
                filterOption: FilterOption,
                filter: FilterState,
              ) => {
                return filter.selectedFilters === filterOption;
              },
              matchInAccumulator: (
                filter: FilterState,
                filterOption: FilterOption,
              ) => {
                if (filter.filterMetaState.isNegated) {
                  //.TODO (jimmy): TECHDEBT centralize the negation key

                  return accumulator[filter.filterMetaState.type][
                    `after-${filterOption}`
                  ];
                }

                return accumulator[filter.filterMetaState.type][filterOption];
              },
            };
          case 'pickup_date':
            return {
              filterOptions: PICKUP_DATE_FILTER_OPTIONS.map(
                (option) => option.value,
              ),
              matchFunction: (
                filterOption: FilterOption,
                filter: FilterState,
              ) => {
                return filter.selectedFilters === filterOption;
              },
              matchInAccumulator: (
                filter: FilterState,
                filterOption: FilterOption,
              ) => {
                if (filter.filterMetaState.isNegated) {
                  //.TODO (jimmy): TECHDEBT centralize the negation key

                  return accumulator[filter.filterMetaState.type][
                    `after-${filterOption}`
                  ];
                }

                return accumulator[filter.filterMetaState.type][filterOption];
              },
            };
          case 'last_updated_date':
            return {
              filterOptions: LAST_UPDATED_DATE_FILTER_OPTIONS.map(
                (option) => option.value,
              ),
              matchFunction: (
                filterOption: FilterOption,
                filter: FilterState,
              ) => {
                return filter.selectedFilters === filterOption;
              },
              matchInAccumulator: (
                filter: FilterState,
                filterOption: FilterOption,
              ) => {
                if (filter.filterMetaState.isNegated) {
                  return accumulator[filter.filterMetaState.type][
                    `after-${filterOption}`
                  ];
                }

                return accumulator[filter.filterMetaState.type][filterOption];
              },
            };
          case 'created_date':
            return {
              filterOptions: CREATED_DATE_FILTER_OPTIONS.map(
                (option) => option.value,
              ),
              matchFunction: (
                filterOption: FilterOption,
                filter: FilterState,
              ) => {
                return filter.selectedFilters === filterOption;
              },
              matchInAccumulator: (
                filter: FilterState,
                filterOption: FilterOption,
              ) => {
                if (filter.filterMetaState.isNegated) {
                  return accumulator[filter.filterMetaState.type][
                    `after-${filterOption}`
                  ];
                }

                return accumulator[filter.filterMetaState.type][filterOption];
              },
            };

          default:
            return {
              filterOptions: [],
              matchFunction: () => false,
            };
        }
      },
      [
        JSON.stringify(financeManagers),
        JSON.stringify(salesReps),
        JSON.stringify(lenders),
        accumulatorAndPreviousAccumulatorAreEqual,
      ],
    );

    const filteredDealsIdsFromFilters = useMemo(() => {
      const _filters = filters.filter((filter) => {
        if (
          filter.filterMetaState.isCurrentlyBeingBuilt &&
          filter.selectedFilters === 'none'
        ) {
          return false;
        }

        if (matchPreference === 'match-all-filters') {
          // UX NOTE: when we're doing a `match-all-filters`
          // we don't want to include filters that are still being built
          // because they start empty and will never match any deals
          // Thus, we filter them out
          return !filter.filterMetaState.isCurrentlyBeingBuilt;
        }

        return true;
      });

      return _filters.map((filter: FilterState) => {
        // TODO: Make this work with the other filters: date filters, and toggle filters
        const { filterOptions, matchFunction, matchInAccumulator } =
          getFilterOptions(filter.filterMetaState.type);

        return filterOptions.reduce((acc, filterOption) => {
          if (!matchFunction(filterOption, filter)) {
            return acc;
          }

          // UX NOTE: if the filter is still being built and empty, don't include it in the deal ids
          // TECH DEBT: this concept should be cleaned up
          if (
            filter.filterMetaState.isCurrentlyBeingBuilt &&
            !Object.values(filter.selectedFilters).some((value) => value)
          ) {
            return acc;
          }

          if (matchInAccumulator && !!filter) {
            const matchInAccumulatorResult = matchInAccumulator(
              filter,
              filterOption,
            );

            if (matchInAccumulatorResult) {
              // @ts-expect-error: union is not a function on Set
              return acc.union(matchInAccumulatorResult);
            }
          }

          const dealIdsFromFilterOption =
            accumulator[filter.filterMetaState.type][filterOption];

          if (!dealIdsFromFilterOption) {
            // DEV NOTE: this can happen when `accumulator` is populated
            return acc;
          }

          // @ts-expect-error: union is not a function on Set
          return acc.union(dealIdsFromFilterOption);
        }, new Set<Deal['id']>([]));
      });
    }, [
      JSON.stringify(filters),
      accumulatorAndPreviousAccumulatorAreEqual,
      JSON.stringify(getFilterOptions),
      JSON.stringify(matchPreference),
    ]);

    const dealIdsMatchingFilters = useMemo(() => {
      if (matchPreference === 'match-any-filter') {
        return filteredDealsIdsFromFilters.reduce((acc, dealIds) => {
          // @ts-expect-error: union is not a function on Set
          return acc.union(dealIds);
        }, new Set<Deal['id']>(filteredDealsIdsFromFilters[0]));
      }

      if (matchPreference === 'match-all-filters') {
        // We need to intersect all the deal ids from the filters
        return filteredDealsIdsFromFilters.reduce((acc, dealIds) => {
          // @ts-expect-error: intersection is not a function on Set
          return acc.intersection(dealIds);
        }, new Set<Deal['id']>(filteredDealsIdsFromFilters[0]));
      }

      return new Set<Deal['id']>();
    }, [filteredDealsIdsFromFilters, matchPreference]);

    const shouldApplyFilteredDealsIds = useMemo(() => {
      return (
        filters.some(
          (filter) => !filter.filterMetaState.isCurrentlyBeingBuilt,
        ) ||
        filters.some((filter) => {
          if (isString(filter.selectedFilters)) {
            return filter.selectedFilters !== 'none';
          }

          return Object.values(filter.selectedFilters).some((value) => value);
        })
      );
    }, [filters]);

    const filterCallback = useCallback(
      (dealLike: DealLike) => {
        if (!shouldApplyFilteredDealsIds) {
          return true;
        }

        return dealIdsMatchingFilters.has(dealLike.id);
      },
      [shouldApplyFilteredDealsIds, dealIdsMatchingFilters],
    );

    previousAccumulator.current = accumulator;

    return (
      <FilteredDealsContext.Provider
        value={{
          accumulator,
          getCount,
          filters,
          setFilters,
          matchPreference,
          setMatchPreference,
          dealIdsMatchingFilters,
          shouldApplyFilteredDealsIds,
          filterCallback,
        }}
      >
        {children}
      </FilteredDealsContext.Provider>
    );
  },
);

export const useFilteredDealsContext = () => {
  const context = useContext(FilteredDealsContext);
  if (!context) {
    throw new Error(
      'useFilteredDealsContext must be used within a FilteredDealsProvider',
    );
  }
  return context;
};
